import React, { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import clsx from 'clsx';
import type { GenericItem, CellValue } from '../types';
import { formatColumnHeader } from '../utils';
import { renderCell, renderColumnGroup } from '../CellRenderers';
import type {
  ColumnElement,
  TableVisualizationConfig,
} from '../../../visualization-schema';

interface UseTableColumnsProps {
  content: { data: GenericItem[] };
  visualizationConfig?: TableVisualizationConfig;
}

export const useTableColumns = ({
  content,
  visualizationConfig,
}: UseTableColumnsProps): ColumnDef<GenericItem>[] => {
  return useMemo(() => {
    if (!content?.data || content.data.length === 0) {
      return [];
    }

    // If we have visualization config with columns, use that
    if (visualizationConfig?.columns) {
      return visualizationConfig.columns.map((col) => ({
        header: col.header,
        accessorKey: col.field,
        cell: ({ row }) => {
          if (col.group) {
            return renderColumnGroup(col.group, row.original);
          }

          if (col.field) {
            return (
              <div
                className={clsx(
                  'max-w-[400px] break-words whitespace-break-spaces',
                  col.className,
                )}
                style={{ width: col.width, ...col.style }}
              >
                {renderCell(
                  row.getValue(col.field),
                  col.columnType,
                  col as ColumnElement,
                  row.original,
                )}
              </div>
            );
          }

          return null;
        },
      })) as ColumnDef<GenericItem>[];
    }

    // Fallback to auto-generated columns
    const keys = Array.from(
      new Set(content.data.flatMap((item) => Object.keys(item))),
    );

    return keys.map((key) => ({
      header: formatColumnHeader(key),
      accessorKey: key,
      size: 400,
      cell: ({ getValue, row }) => {
        // Try to infer column type from key name
        const inferredType = inferColumnType(key);
        return (
          <div className="max-w-[400px] break-words whitespace-break-spaces">
            {renderCell(
              getValue() as CellValue,
              inferredType,
              undefined,
              row.original,
            )}
          </div>
        );
      },
    })) as ColumnDef<GenericItem>[];
  }, [content?.data, visualizationConfig]);
};

// Helper function to infer column type from key name
const inferColumnType = (key: string): string | undefined => {
  if (key.includes('status')) {
    return 'status';
  }
  if (key.includes('severity')) {
    return 'severity';
  }
  if (key === 'id') {
    return 'id';
  }
  if (key.includes('date') || key.includes('time')) {
    return 'date';
  }
  if (key.includes('image') || key.includes('avatar') || key.includes('icon')) {
    return 'image';
  }
  if (key.includes('url') || key.includes('link')) {
    return 'badge';
  }
  {
    return undefined;
  }
};
